import type { JSX } from '@stencil/core';
import { Component, Element, h, Method, Prop, State, Watch } from '@stencil/core';
import type {
	AlignPropType,
	ButtonCallbacksPropType,
	FocusableElement,
	KoliBriTabsCallbacks,
	LabelPropType,
	StencilUnknown,
	Stringified,
	TabBehaviorPropType,
	TabButtonProps,
	TabsAPI,
	TabsStates,
} from '../../schema';
import {
	devHint,
	koliBriQuerySelector,
	setState,
	uiUxHintMillerscheZahl,
	validateAlign,
	validateLabel,
	validateTabBehavior,
	watchJsonArrayString,
	watchNumber,
} from '../../schema';

import type { Generic } from 'adopted-style-sheets';
import { KolButtonWcTag } from '../../core/component-names';
import { translate } from '../../i18n';
import { KeyboardKey } from '../../schema/enums';
import type { HasCreateButtonPropType } from '../../schema/props/has-create-button';
import { validateHasCreateButton } from '../../schema/props/has-create-button';
import clsx from '../../utils/clsx';
import { delegateClick, setClick } from '../../utils/element-click';
import { delegateFocus, setFocus } from '../../utils/element-focus';
import { dispatchDomEvent, KolEvent } from '../../utils/events';
// https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-2/tabs.html

/**
 * The **Tabs** component is used to organize related content on the same page and navigate between them. Tabs ensure that large amounts of content can be more easily organized for users.
 */
@Component({
	tag: 'kol-tabs',
	styleUrls: {
		default: './style.scss',
	},
	shadow: true,
})
export class KolTabs implements TabsAPI, FocusableElement {
	@Element() private readonly host?: HTMLKolTabsElement;
	private tabPanelsElement?: HTMLElement;
	private onCreateLabel = `${translate('kol-new')} …`;
	private currentFocusIndex: number | undefined;
	private currentTabButtonRef?: HTMLKolButtonWcElement;

	private nextPossibleTabIndex = (tabs: TabButtonProps[], offset: number, step = 1): number => {
		const nextOffset = offset + step;

		if (nextOffset < tabs.length) {
			if (tabs[nextOffset]._disabled) {
				return this.nextPossibleTabIndex(tabs, offset, step + 1);
			}
			return nextOffset;
		}

		return offset;
	};

	private prevPossibleTabIndex = (tabs: TabButtonProps[], offset: number, step = 1): number => {
		const nextOffset = offset - step;

		if (nextOffset >= 0) {
			if (tabs[nextOffset]._disabled) {
				return this.prevPossibleTabIndex(tabs, offset, step + 1);
			}
			return nextOffset;
		}

		return offset;
	};

	private onKeyDown = (event: KeyboardEvent) => {
		switch (event.key as KeyboardKey) {
			case KeyboardKey.ArrowRight:
				this.goToNextTab(event);
				break;
			case KeyboardKey.ArrowLeft:
				this.goToPreviousTab(event);
				break;
			case KeyboardKey.Space:
			case KeyboardKey.Enter:
				this.activateFocusedTab(event);
				break;
		}
	};

	private getCurrentFocusIndex(): number {
		if (typeof this.currentFocusIndex === 'number') {
			return this.currentFocusIndex;
		}

		return this.state._selected;
	}

	private getKeyboardTabChangeMode(): 'selectFocusOnly' | 'activateCompletely' {
		if (this._behavior === 'select-manual') {
			return 'selectFocusOnly';
		}

		return 'activateCompletely';
	}

	private goToNextTab(event: KeyboardEvent) {
		const nextFocusIndex = this.nextPossibleTabIndex(this.state._tabs, this.getCurrentFocusIndex());
		this.selectNextTabEvent(event, nextFocusIndex, this.getKeyboardTabChangeMode());
	}

	private goToPreviousTab(event: KeyboardEvent) {
		const nextFocusIndex = this.prevPossibleTabIndex(this.state._tabs, this.getCurrentFocusIndex());
		this.selectNextTabEvent(event, nextFocusIndex, this.getKeyboardTabChangeMode());
	}

	private activateFocusedTab(event: KeyboardEvent) {
		if (typeof this.currentFocusIndex === 'number') {
			this.onSelect(event, this.currentFocusIndex);
		}
	}

	private readonly onClickSelect = (event: MouseEvent, index: number): void => {
		this.selectNextTabEvent(event, index);
	};

	private selectNextTabEvent(
		event: KeyboardEvent | MouseEvent,
		nextTabIndex: number,
		changeMode: 'selectFocusOnly' | 'activateCompletely' = 'activateCompletely',
	): void {
		this.currentFocusIndex = nextTabIndex;

		this.focusTabById(nextTabIndex);

		if (changeMode === 'activateCompletely') {
			this._selected = nextTabIndex;

			const tab = this.state._tabs[nextTabIndex];
			tab._on?.onSelect?.(event, nextTabIndex);

			this.onSelect(event, nextTabIndex);
		}
	}

	private readonly onMouseDown = (event: Event): void => {
		event.preventDefault();
	};

	private readonly callbacks: ButtonCallbacksPropType<number> = {
		onClick: this.onClickSelect,
		onMouseDown: this.onMouseDown,
	};

	/**
	 * Sets focus on the current tab button.
	 */
	@Method()
	public async focus(): Promise<void> {
		return delegateFocus(this.host!, () => setFocus(this.currentTabButtonRef!));
	}

	/**
	 * Triggers a click on the currently selected tab.
	 */
	@Method()
	public async click(): Promise<void> {
		return delegateClick(this.host!, async () => setClick(this.currentTabButtonRef!));
	}

	private readonly setCurrentTabButtonRef = (ref?: HTMLKolButtonWcElement) => {
		this.currentTabButtonRef = ref;
	};

	private renderButtonGroup() {
		return (
			// Rule is disabled, because KolButtonWc is focusable.
			// eslint-disable-next-line jsx-a11y/interactive-supports-focus
			<div aria-label={this.state._label} class="kol-tabs__button-group" role="tablist" onKeyDown={this.onKeyDown} onBlur={this.onBlur}>
				{this.state._tabs.map((button: TabButtonProps, index: number) => (
					<KolButtonWcTag
						ref={this.state._selected === index ? this.setCurrentTabButtonRef : undefined}
						_disabled={button._disabled}
						_icons={button._icons}
						_hideLabel={button._hideLabel}
						_label={button._label} // TODO: ariaLabel-Konzept prüfen
						_on={this.callbacks as ButtonCallbacksPropType<StencilUnknown>}
						_tabIndex={this.state._selected === index ? 0 : -1}
						_tooltipAlign={button._tooltipAlign}
						_variant={this.state._selected === index ? 'custom' : undefined}
						_customClass={this.state._selected === index ? 'selected' : ''}
						_ariaControls={`tabpanel-${index}`}
						_ariaSelected={this.state._selected === index}
						_id={`${this.state._label.replace(/\s/g, '-')}-tab-${index}`}
						_role="tab"
						_value={index}
					></KolButtonWcTag>
				))}
				{this.state._hasCreateButton && (
					<KolButtonWcTag
						class="kol-tabs__button-create"
						_label={this.onCreateLabel}
						_on={{
							onClick: this.onCreate,
						}}
						_icons="kolicon-plus"
						data-testid="tabs-create-button"
					></KolButtonWcTag>
				)}
			</div>
		);
	}

	private tabPanelHost?: HTMLDivElement;

	private readonly catchTabPanelHost = (el?: HTMLDivElement) => {
		this.tabPanelHost = el;
	};

	public render(): JSX.Element {
		return (
			<div
				ref={(el) => {
					this.tabPanelsElement = el;
				}}
				class={clsx('kol-tabs', `kol-tabs--align-${this.state._align}`)}
			>
				{this.renderButtonGroup()}
				<div class="kol-tabs__content" ref={this.catchTabPanelHost}>
					{/* <slot /> */}
				</div>
			</div>
		);
	}

	/**
	 * Defines the visual orientation of the component.
	 */
	@Prop() public _align?: AlignPropType = 'top';

	/**
	 * Defines which behavior is active.
	 */
	@Prop() public _behavior?: TabBehaviorPropType;

	/**
	 * Defines whether the element has a create button.
	 */
	@Prop() public _hasCreateButton?: HasCreateButtonPropType = false;

	/**
	 * Defines the visible or semantic label of the component (e.g. aria-label, label, headline, caption, summary, etc.).
	 */
	@Prop() public _label!: LabelPropType;

	/**
	 * Gibt die Liste der Callback-Funktionen an, die auf Events aufgerufen werden sollen.
	 */
	@Prop() public _on?: KoliBriTabsCallbacks;

	/**
	 * Defines which tab is active.
	 */
	@Prop({ mutable: true, reflect: true }) public _selected?: number = 0;

	/**
	 * Defines the tab captions.
	 */
	@Prop() public _tabs!: Stringified<TabButtonProps[]>;

	@State() public state: TabsStates = {
		_align: 'top',
		_label: '', // ⚠ required
		_selected: 0,
		_tabs: [],
	};

	private selectNextNotDisabledTab = (selected: number, tabs: TabButtonProps[], upOrDown = true, initialSelected?: number): number => {
		if (selected > tabs.length - 1) {
			selected = tabs.length - 1;
		}
		if (selected < 0) {
			selected = 0;
		}
		if (Array.isArray(tabs) && tabs[selected]) {
			if (tabs[selected]._disabled) {
				if (upOrDown === true) {
					if (selected < tabs.length - 1) {
						return this.selectNextNotDisabledTab(selected + 1, tabs, true, initialSelected || selected);
					} else {
						selected = initialSelected || selected;
						upOrDown = false;
					}
				}
				if (upOrDown === false) {
					if (selected > 0) {
						return this.selectNextNotDisabledTab(selected - 1, tabs, false, initialSelected || selected);
					} else {
						devHint(`[KolTabs] All tabs are disabled, and therefore no tab can be displayed.`);
					}
				}
			}
		}
		return selected;
	};

	private syncSelectedAndTabs = (nextValue: unknown, nextState: Map<string, unknown>, _component: Generic.Element.Component, key: string) => {
		let selected: number;
		if (key === '_selected') {
			selected = nextValue as number;
		} else {
			selected = this.state._selected;
		}
		let tabs: TabButtonProps[];
		if (key === '_tabs') {
			tabs = nextValue as TabButtonProps[];
		} else {
			tabs = this.state._tabs;
		}
		if (tabs.length > 0) {
			nextState.set('_selected', this.selectNextNotDisabledTab(selected, tabs));
		}
	};

	@Watch('_align')
	public validateAlign(value?: AlignPropType) {
		validateAlign(this, value);
	}

	@Watch('_behavior')
	public validateBehavior(value?: TabBehaviorPropType) {
		validateTabBehavior(this, value);
	}

	@Watch('_hasCreateButton')
	public validateHasCreateButton(value?: HasCreateButtonPropType) {
		validateHasCreateButton(this, value);
	}

	@Watch('_label')
	public validateLabel(value?: LabelPropType): void {
		validateLabel(this, value, {
			required: true,
		});
	}

	@Watch('_on')
	public validateOn(value?: KoliBriTabsCallbacks): void {
		if (typeof value === 'object' && value !== null) {
			const callbacks: KoliBriTabsCallbacks = {};
			if (typeof value.onCreate === 'function') {
				callbacks.onCreate = value.onCreate;
			}
			if (typeof value.onSelect === 'function') {
				callbacks.onSelect = value.onSelect;
			}
			setState<KoliBriTabsCallbacks>(this, '_on', callbacks);
		}
	}

	@Watch('_selected')
	public validateSelected(value?: number): void {
		watchNumber(this, '_selected', value, {
			hooks: {
				beforePatch: this.syncSelectedAndTabs,
			},
		});
	}

	@Watch('_tabs')
	public validateTabs(value?: Stringified<TabButtonProps[]>): void {
		watchJsonArrayString(
			this,
			'_tabs',
			(item: TabButtonProps) => typeof item === 'object' && item !== null && typeof item._label === 'string' && item._label.length > 0,
			value,
			undefined,
			{
				hooks: {
					beforePatch: this.syncSelectedAndTabs,
					afterPatch: this.refreshTabPanels,
				},
			},
		);
		uiUxHintMillerscheZahl('KolTabs', this.state._tabs.length);
	}

	public componentWillLoad(): void {
		this.validateAlign(this._align);
		this.validateLabel(this._label);
		this.validateOn(this._on);
		this.validateSelected(this._selected);
		this.validateTabs(this._tabs);
		this.validateBehavior(this._behavior);
		this.validateHasCreateButton(this._hasCreateButton);
	}

	private refreshTabPanels = () => {
		if (!this.tabPanelHost) return;
		// Clear existing panels
		while (this.tabPanelHost.firstChild) {
			this.tabPanelHost.removeChild(this.tabPanelHost.firstChild);
		}
		for (let i = 0; i < this.state._tabs?.length; i++) {
			const div = document.createElement('div');
			div.setAttribute('aria-labelledby', `${this.state._label.replace(/\s/g, '-')}-tab-${i}`);
			div.setAttribute('id', `tabpanel-${i}`);
			div.setAttribute('role', 'tabpanel');
			div.setAttribute('hidden', '');
			div.setAttribute('tabindex', '0');
			const slot = document.createElement('slot');
			slot.setAttribute('name', `tabpanel-slot-${i}`);
			div.appendChild(slot);
			this.tabPanelHost?.appendChild(div);

			if (typeof HTMLCollection !== 'undefined' && this.host?.children instanceof HTMLCollection && this.host?.children[i] /* SSR instanceof HTMLElement */) {
				this.host.children[i].setAttribute('slot', `tabpanel-slot-${i}`);
			}
		}
		this.updateVisiblePanel();
	};

	private updateVisiblePanel = () => {
		if (!this.tabPanelHost) return;
		Array.from(this.tabPanelHost.children).forEach((child, i) => {
			if (i === this.state._selected) {
				child.removeAttribute('hidden');
			} else {
				child.setAttribute('hidden', '');
			}
		});
	};

	public componentDidRender(): void {
		this.refreshTabPanels();
	}

	private focusTabById(index: number): void {
		if (this.tabPanelsElement /* SSR instanceof HTMLElement */) {
			const button: HTMLElement | null = koliBriQuerySelector(`button#${this.state._label.replace(/\s/g, '-')}-tab-${index}`, this.tabPanelsElement);
			button?.focus();
		}
	}

	private onSelect(event: CustomEvent | KeyboardEvent | MouseEvent | PointerEvent, index: number): void {
		this._on?.onSelect?.(event, index);
		if (this.host) {
			dispatchDomEvent(this.host, KolEvent.select, index);
		}

		this.focusTabById(index);
	}

	private onCreate = (event: Event) => {
		event.preventDefault();
		this.state._on?.onCreate?.(event);
		if (this.host) {
			dispatchDomEvent(this.host, KolEvent.create);
		}
	};

	private onBlur = () => {
		this.currentFocusIndex = undefined;
	};
}
